import { ColDef, ColumnState } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { autoSizeAllColumns } from 'src/utils/grid/ColumnSizeUtils';
import { showSuccess } from 'src/utils/toast/toastUtil';

interface GridContextWrapperProps<TData extends { inserted?: boolean } = any> {
	children: React.ReactNode;
	gridWrapperRef?: React.RefObject<HTMLDivElement>;
	gridRef: React.RefObject<AgGridReact<TData> | null>;
	paddedGridData: TData[];
	setPaddedGridData?: (gridData: any[]) => void;				//추가한 행 초기화(+- 그리드에서 적용)
	initSelected: () => void;									//선택한 행 초기화(스토어에서 설정, 모든 그리드에서 적용)
	defaultRowData: TData;
	allSelected?: () => void;									//모든 행 선택(+- 그리드에서 적용)(스토어에서 설정, 모든 그리드에서 적용)
	baseColumnDefs?: ColDef<TData>[];									//그리드의 기본 컬럼 정의
	setColumnState?: (state: ColumnState[] | null) => void;		//그리드의 컬럼 상태 저장
	localStorageKey?: string;									//그리드의 컬럼 상태 저장 키(로컬스토리지에서 관리하는)
	// 우클릭 드롭다운에 포함할 추가 메뉴 아이템들(기본 메뉴 아이템들 외에 추가 메뉴 아이템들)
	menuItems?: Array<{
		label: string;
		onClick: (rowData?: TData, rowIndex?: number) => void;
	}>;
	// 공통 행 드래그 옵션
	enableRowDrag?: boolean;
	dragEntireRow?: boolean;
	onAfterReorder?: (list: TData[]) => void;
	// 엑셀 붙여넣기 관련 옵션
	enableExcelPaste?: boolean;									// 엑셀 붙여넣기 기능 활성화
	onExcelPaste?: (parsedData: string[][], 
		position?: { 
			startRowIndex: number; 
			startColIndex: number; 
			focusedColId?: string }) => void;			// 파싱된 엑셀 데이터 처리 콜백
	// 데이터 히스토리 관련 옵션
	enableDataHistory?: boolean;								// 데이터 히스토리 기능 활성화 (기본값: true)
	historyLimit?: number;										// 히스토리 최대 저장 개수 (기본값: 10)
	bulkChangeThreshold?: number;								// 대량 변경으로 판단할 최소 변경 개수 (기본값: 3)
	onHistorySave?: (saveHistoryFn: (data: TData[]) => void) => void; // 히스토리 저장 함수를 외부에 노출
	contextMenuZIndex?: number;									// 모달 위에 컨텍스트 메뉴를 표시하기 위한 z-index
}

const GridContextWrapperImpl = <TData extends { inserted?: boolean } = any>(props: GridContextWrapperProps<TData>) => {
	const {
		children, 
		gridRef,	
		gridWrapperRef,
		paddedGridData, 
		setPaddedGridData,
		initSelected,
		defaultRowData,
		allSelected = undefined,
		baseColumnDefs,
		setColumnState,
		localStorageKey,
		enableExcelPaste = false,
		onExcelPaste,
		enableDataHistory = true,
		historyLimit = 10,
		bulkChangeThreshold = 3,
		onHistorySave,
		contextMenuZIndex = 10060,
		menuItems = [], 
		enableRowDrag = false,
		dragEntireRow = true,
		onAfterReorder,
	} = props;
	const wrapperRef = useRef<HTMLDivElement>(null);
	const menuRef = useRef<HTMLDivElement>(null);
	
	// 데이터 히스토리 관리
	const [dataHistory, setDataHistory] = useState<TData[][]>([]);
	const [historyIndex, setHistoryIndex] = useState(-1);
	const lastDataLength = useRef(paddedGridData?.length || 0);
	const isUndoingRef = useRef(false); // 되돌리기 중인지 나타내는 플래그
	// 컨텍스트 메뉴 상태
	const [contextMenu, setContextMenu] = useState<{
		visible: boolean;
		x: number;
		y: number;
		rowData?: TData;
		rowIndex?: number;
	}>({
		visible: false,
		x: 0,
		y: 0,
		rowData: undefined,
		rowIndex: undefined
	});

	// 컨텍스트 메뉴 닫기
	const closeContextMenu = useCallback(() => { setContextMenu({ visible: false, x: 0, y: 0, rowData: undefined, rowIndex: undefined }); }, []);

	// 히스토리에 현재 상태 저장
	const saveToHistory = useCallback((data: TData[]) => {
		if (!enableDataHistory) return;
		
		const newHistory = dataHistory.slice(0, historyIndex + 1);
		newHistory.push([...data]);
		
		// 제한 초과시 오래된 히스토리 제거
		if (newHistory.length > historyLimit) {
			newHistory.shift();
			// 인덱스는 그대로 유지 (가장 최신이 마지막 인덱스)
			setHistoryIndex(newHistory.length - 1);
		} else {
			setHistoryIndex(newHistory.length - 1);
		}
		
		setDataHistory(newHistory);
		console.log('🕰️ 히스토리 저장:', newHistory.length, '개 상태 저장됨, 인덱스:', newHistory.length - 1);
		
	}, [enableDataHistory, dataHistory, historyIndex, historyLimit]);

	// 히스토리 저장 함수를 외부에 노출
	useEffect(() => {
		if (enableDataHistory && onHistorySave) {
			onHistorySave(saveToHistory);
		}
	}, [enableDataHistory, onHistorySave, saveToHistory]);

	// 되돌리기 실행
	const undoDataChange = useCallback(() => {
		console.log('🔍 되돌리기 시도 - 히스토리:', dataHistory.length, '개, 현재 인덱스:', historyIndex);
		
		if (!enableDataHistory || dataHistory.length < 2 || historyIndex < 1) {
			console.log('❌ 되돌리기 불가 - 조건 불충족');
			return false;
		}
		
		const previousData = dataHistory[historyIndex - 1];
		if (previousData && setPaddedGridData) {
			isUndoingRef.current = true; // 되돌리기 시작
			setPaddedGridData([...previousData]);
			setHistoryIndex(prev => prev - 1);
			lastDataLength.current = previousData.length;
			console.log('✅ 데이터 되돌리기 실행:', previousData.length, '개 행으로 복원, 새 인덱스:', historyIndex - 1);
			
			// 되돌리기 완료 후 플래그 해제
			setTimeout(() => {
				isUndoingRef.current = false;
			}, 100);
			
			return true;
		}
		return false;
	}, [enableDataHistory, dataHistory, historyIndex, setPaddedGridData]);

	// 대량 변경 자동 감지 및 히스토리 저장하는 setPaddedGridData 래퍼
	const enhancedSetPaddedGridData = useCallback((newData: TData[]) => {
		if (!setPaddedGridData) return;
		
		const currentLength = paddedGridData?.length || 0;
		const newLength = newData?.length || 0;
		const lengthDiff = Math.abs(newLength - currentLength);
		
		// 되돌리기 중이면 히스토리 저장하지 않고 바로 업데이트
		if (isUndoingRef.current) {
			console.log('↩️ 되돌리기 중 - 직접 데이터 업데이트:', currentLength, '→', newLength);
			setPaddedGridData(newData);
			return;
		}
		
		// 대량 변경 감지 (임계값 이상 변경 시)
		if (enableDataHistory && lengthDiff >= bulkChangeThreshold) {
			console.log('📊 대량 변경 감지:', currentLength, '→', newLength, `(차이: ${lengthDiff})`);
			saveToHistory(paddedGridData || []);
		}
		
		lastDataLength.current = newLength;
		setPaddedGridData(newData);
	}, [paddedGridData, setPaddedGridData, enableDataHistory, bulkChangeThreshold, saveToHistory, isUndoingRef]);

	// 엑셀 붙여넣기 로직 (공통)
	const handleExcelPaste = useCallback((e: ClipboardEvent) => {
		if (!enableExcelPaste || !onExcelPaste) return;

		e.preventDefault();

		const clipboardData = e.clipboardData?.getData('text');
		if (!clipboardData) {
			console.warn('📋 GridContextWrapper: 클립보드 데이터 없음');
			return;
		}

		try {
			// 엑셀 데이터 파싱 (공통 로직)
			const rows = clipboardData.trim().split('\n');
			const parsedData: string[][] = []; // 파싱된 데이터를 저장할 배열

			// 각 행을 파싱하여 유효한 행만 배열에 추가
			rows.forEach((row) => {
				const cells = row.split('\t').map(cell => cell.trim()); // 각 행을 탭으로 분리하여 셀 데이터 추출
				// 첫 번째 셀에 데이터가 있어야 유효한 행으로 판단
				if (cells[0]) {
					parsedData.push(cells);
				}
			});

			if(parsedData.length > 0) {
				const gridApi = gridRef.current?.api;
				let startRowIndex = 0; // 붙여넣기 시작 행 인덱스
				let startColIndex = 0; // 붙여넣기 시작 열 인덱스
				let focusedColId = ''; // 붙여넣기 시작 열 ID

				if(gridApi){
					const focusedCell = gridApi.getFocusedCell();
					if(focusedCell) {
						startRowIndex = focusedCell.rowIndex;
						startColIndex = 0; // 각 페이지에서 계산 (여기서 값을 가져오지 않음)
						focusedColId = focusedCell.column?.getColId() || '';
					}
				}

				onExcelPaste(parsedData, { startRowIndex, startColIndex, focusedColId });
			}

		} catch (error) {
			console.error('📋 GridContextWrapper: 붙여넣기 에러:', error);
		}
	}, [enableExcelPaste, onExcelPaste]);

	// 붙여넣기 이벤트 리스너 등록
	useEffect(() => {
		if (!enableExcelPaste) return;

		const gridElement = gridWrapperRef?.current;
		if (gridElement) {
			gridElement.addEventListener('paste', handleExcelPaste as any);
			gridElement.setAttribute('tabindex', '0');


			return () => {
				gridElement.removeEventListener('paste', handleExcelPaste );
			};
		}
	}, [enableExcelPaste, handleExcelPaste, gridWrapperRef]);

	// Ctrl+Z 되돌리기 이벤트 리스너
	useEffect(() => {
		if (!enableDataHistory) return;

		const handleKeyDown = (e: KeyboardEvent) => {
			// Ctrl+Z 감지 (Shift는 제외 - Ctrl+Shift+Z는 Redo용)
			if (e.ctrlKey && e.key === 'z' && !e.shiftKey) {
				// 그리드 영역에 포커스가 있거나 그리드 내부에 포커스가 있는 경우에만
				const wrapperElement = gridWrapperRef?.current || wrapperRef.current;
				const focusedElement = document.activeElement;
				
				if (wrapperElement && (
					wrapperElement.contains(focusedElement) || 
					wrapperElement === focusedElement
				)) {
					e.preventDefault();
					e.stopPropagation();
					
					console.log('⌨️ Ctrl+Z 감지됨 - 되돌리기 시도');
					const success = undoDataChange();
					if (success) {
						showSuccess('📋 데이터 변경사항이 되돌려졌습니다.');
					} else {
						console.log('↩️ 되돌릴 수 있는 변경사항이 없습니다.');
					}
				}
			}
		};

		document.addEventListener('keydown', handleKeyDown, true); // capture phase에서 처리
		return () => document.removeEventListener('keydown', handleKeyDown, true);
	}, [enableDataHistory, undoDataChange, gridWrapperRef]);

	// 우클릭 이벤트 처리
	const handleContextMenu = useCallback((e: React.MouseEvent) => {
		e.preventDefault();
		setContextMenu({
			visible: true,
			x: e.clientX,
			y: e.clientY,
			rowData: undefined,
			rowIndex: undefined
		});
	}, []);
	

	// 클릭 시 컨텍스트 메뉴 닫기
	useEffect(() => {
		const handleOutsideClick = (e: MouseEvent) => {
			if (menuRef.current && menuRef.current.contains(e.target as Node)) {
				return;
			}
			closeContextMenu();
		};
		if (contextMenu.visible) {
			document.addEventListener('mousedown', handleOutsideClick);
			return () => document.removeEventListener('mousedown', handleOutsideClick);
		}
	}, [contextMenu.visible, closeContextMenu]);

	// 컬럼 초기화 함수
	const resetColumnState = useCallback(() => {
		if (!gridRef || !baseColumnDefs || !setColumnState) return;

		// 1. 로컬스토리지에서 컬럼 상태 제거
		if (localStorageKey) {
			localStorage.removeItem(localStorageKey);
		}
		
		// 2. 스토어의 columnState를 null로 설정
		setColumnState(null);
		
		// 3. 컴포넌트 리렌더링 후 AG Grid 초기화를 위해 setTimeout 사용
		setTimeout(() => {
			if (gridRef.current?.api) {
				// baseColumnDefs에서 모든 컬럼 ID 추출
				const allColumnIds = baseColumnDefs
					.map(col => col.colId)
					.filter((colId): colId is string => colId !== undefined);
				
				// 모든 컬럼을 강제로 표시
				gridRef.current.api.setColumnsVisible(allColumnIds, true);
				
				// 컬럼 상태 초기화
				gridRef.current.api.resetColumnState();
			}
		}, 0);
		
		closeContextMenu();
	}, [gridRef, baseColumnDefs, setColumnState, localStorageKey, closeContextMenu]);

	
	const saveColumnStateAfterResize = useCallback(() => {
		if (gridRef?.current?.api && localStorageKey && setColumnState) {
			const state = gridRef.current.api.getColumnState();
			if (state) {
				localStorage.setItem(localStorageKey, JSON.stringify(state));
				setColumnState(state);
			}
		}
	}, [gridRef, localStorageKey, setColumnState]);


	const deleteAddedRow = useCallback(() => {
		if (!gridRef?.current || !setPaddedGridData) return;

		const newPaddedGridData: any[] = [];

		// 그리드에 inserted가 모두 true인 경우 삭제 불가, 첫행만 남김
		const isAllInserted = paddedGridData.every(item => item.inserted);
		if(isAllInserted) {
			newPaddedGridData.push(paddedGridData[0]);
		}else{
			paddedGridData.forEach(item => {
				if(item.inserted) {
					return;
				}
				newPaddedGridData.push(item);
			});
		}
		setPaddedGridData(newPaddedGridData);


	}, [gridRef, paddedGridData, setPaddedGridData]);

	const deleteAddedRowOption = useCallback(() => {
		if (!setPaddedGridData) return undefined;
		
		return {
			label: '추가한 행 삭제',
			onClick: () => { deleteAddedRow(); }
		}
	}, [deleteAddedRow, setPaddedGridData]);

	const deleteAddedRowsBelow = useCallback((rowIndex?: number) => {
		if (rowIndex === undefined || rowIndex === null || !gridRef?.current?.api || !setPaddedGridData || !paddedGridData) return;

		const api = gridRef.current.api;
		const nodesToDelete = new Set<any>();
		
		api.forEachNodeAfterFilterAndSort((node: any) => {
			if (node.rowIndex !== null && node.rowIndex > rowIndex && node.data?.inserted) {
				nodesToDelete.add(node.data);
			}
		});

		if (nodesToDelete.size === 0) return;

		const newPaddedGridData = paddedGridData.filter(item => !nodesToDelete.has(item));
		setPaddedGridData(newPaddedGridData);
	}, [gridRef, paddedGridData, setPaddedGridData]);

	const deleteAddedRowsBelowOption = useCallback(() => {
		if (!setPaddedGridData) return undefined;
		
		return {
			label: '아래 추가한 행 삭제',
			onClick: (rowData?: TData, rowIndex?: number) => { deleteAddedRowsBelow(rowIndex); }
		}
	}, [deleteAddedRowsBelow, setPaddedGridData]);

	const deleteUnmodifiedAddedRows = useCallback(() => {
		if (!gridRef?.current || !setPaddedGridData || !defaultRowData) return;

		const newPaddedGridData: any[] = [];
		const isAllInserted = paddedGridData.every(item => item.inserted);

		paddedGridData.forEach(item => {
			if (item.inserted) {
				let isModified = false;
				for (const key in defaultRowData) {
					if (['valueIndex', 'orderIndex', 'orderIndexSeq', 'inserted', 'isModified', 'rowStatus'].includes(key)) continue;
					if (item[key] !== (defaultRowData as any)[key]) {
						isModified = true;
						break;
					}
				}
				if (isModified) {
					newPaddedGridData.push(item);
				}
			} else {
				newPaddedGridData.push(item);
			}
		});

		if (isAllInserted && newPaddedGridData.length === 0 && paddedGridData.length > 0) {
			newPaddedGridData.push(paddedGridData[0]);
		}

		setPaddedGridData(newPaddedGridData);
	}, [gridRef, paddedGridData, setPaddedGridData, defaultRowData]);

	const deleteUnmodifiedAddedRowsOption = useCallback(() => {
		if (!setPaddedGridData) return undefined;
		
		return {
			label: '빈 행 제거',
			
			onClick: () => { deleteUnmodifiedAddedRows(); }
		}
	}, [deleteUnmodifiedAddedRows, setPaddedGridData]);

	// 우클릭 기본 메뉴 아이템들
	const defaultMenuItems = [];
	if (gridRef && baseColumnDefs && setColumnState) {
		defaultMenuItems.push({	label: '컬럼 초기화',	onClick: resetColumnState	});
		defaultMenuItems.push({	label: '너비 맞추기',	onClick: () => {
				if (!gridRef?.current) return;
				autoSizeAllColumns(gridRef.current.api, { saveStateCallback: saveColumnStateAfterResize });
			}
		});
		defaultMenuItems.push({	label: '선택 행 해제',onClick: () => {
				if (!gridRef?.current) return;
				initSelected();
			}
		});
		//grid_Excel 전용 메뉴
		if (setPaddedGridData){
			defaultMenuItems.push(deleteAddedRowOption());
			defaultMenuItems.push(deleteAddedRowsBelowOption());
			defaultMenuItems.push(deleteUnmodifiedAddedRowsOption());
		}
	}

	const allMenuItems = [...defaultMenuItems, ...menuItems];

	// 행 드래그: 드랍 후 순서 재계산 처리
	const handleRowDragEnd = useCallback((e: any) => {
		if (!enableRowDrag) return;
		const api = gridRef.current?.api || e?.api;
		if (!api) return;
		if (api.applyColumnState) {
			api.applyColumnState({ defaultState: { sort: null as any, sortIndex: null as any } });
		}
		
		// defaultRowData에서 orderIndex와 orderIndexSeq 필드명 찾기
		let orderIndexField = 'orderIndex';
		let orderIndexSeqField = 'orderIndexSeq';
		
		if (defaultRowData) {
			const keys = Object.keys(defaultRowData);
			const foundOrderIndex = keys.find(k => k.toLowerCase().includes('orderindex') && !k.toLowerCase().includes('seq'));
			const foundOrderIndexSeq = keys.find(k => k.toLowerCase().includes('orderindexseq'));
			
			if (foundOrderIndex) orderIndexField = foundOrderIndex;
			if (foundOrderIndexSeq) orderIndexSeqField = foundOrderIndexSeq;
		}

		const reordered: TData[] = [] as any;
		api.forEachNodeAfterFilterAndSort((node: any, idx: number) => {
			if (!node?.data) return;
			reordered.push({
				...node.data,
				[orderIndexField]: idx + 1,
				[orderIndexSeqField]: 0,
				// 표준 필드도 함께 갱신하여 공통 postSortRows 로직과 정렬 충돌 방지
				orderIndex: idx + 1,
				orderIndexSeq: 0,
			} as TData);
		});
		if (setPaddedGridData) setPaddedGridData(reordered);
		if (onAfterReorder) onAfterReorder(reordered);
	}, [enableRowDrag, gridRef, setPaddedGridData, defaultRowData, onAfterReorder]);

	// children에 히스토리 관리 및 드래그 props 주입
	const enhancedChildren = React.useMemo(() => {
		const injector = (child: any) => {
			if (!React.isValidElement(child)) return child;
			
			let cloneProps: any = {};
			
			// 히스토리 기능이 활성화된 경우 enhancedSetPaddedGridData 주입
			if (enableDataHistory && setPaddedGridData) {
				cloneProps.setPaddedGridData = enhancedSetPaddedGridData;
			}
			
			// 드래그 기능이 활성화된 경우 드래그 props 주입
			if (enableRowDrag) {
				const prevOnRowDragEnd = (child.props as any)?.onRowDragEnd;
				cloneProps = {
					...cloneProps,
					rowDragManaged: true as any,
					rowDragEntireRow: dragEntireRow as any,
					onRowDragEnd: (e: any) => {
						if (typeof prevOnRowDragEnd === 'function') prevOnRowDragEnd(e);
						handleRowDragEnd(e);
					},
				};
			}

			// 셀 우클릭 시 rowData를 컨텍스트 메뉴 상태에 저장
			const prevOnCellContextMenu = (child.props as any)?.onCellContextMenu;
			cloneProps.onCellContextMenu = (e: any) => {
				if (typeof prevOnCellContextMenu === 'function') prevOnCellContextMenu(e);
				e.event?.preventDefault();
				e.event?.stopPropagation();
				setContextMenu({
					visible: true,
					x: e.event.clientX,
					y: e.event.clientY,
					rowData: e.data,
					rowIndex: e.rowIndex
				});
			};
			
			// props가 있는 경우에만 cloneElement
			if (Object.keys(cloneProps).length > 0) {
				return React.cloneElement(child as any, cloneProps);
			}
			
			return child;
		};
		return React.Children.map(children as any, injector);
	}, [children, contextMenuZIndex, enableDataHistory, enhancedSetPaddedGridData, setPaddedGridData, enableRowDrag, dragEntireRow, handleRowDragEnd]);

	return (
		<div ref={gridWrapperRef} style={{ height: '100%', width: '100%' }}>
			<div 
				ref={wrapperRef}
				className="ag-theme-alpine w-full h-full flex flex-col overflow-y-auto z-0 relative" 
				onContextMenu={handleContextMenu}
			>
				{enhancedChildren}
				
				{/* 커스텀 컨텍스트 메뉴 */}
				{contextMenu.visible && allMenuItems.length > 0 && ReactDOM.createPortal(
					<div
						ref={menuRef}
						className="fixed bg-white border border-gray-300 shadow-lg rounded-md py-1"
						style={{
							left: contextMenu.x,
							top: contextMenu.y,
							minWidth: '120px',
							zIndex: contextMenuZIndex
						}}
						onContextMenu={(e) => e.preventDefault()}
					>
						{allMenuItems.map((item, index) => (
							<div
								key={index}
								className="px-3 py-2 hover:bg-gray-100 cursor-pointer font-size-10"
								onClick={() => {
									item?.onClick(contextMenu.rowData, contextMenu.rowIndex);
									closeContextMenu();
								}}
							>
								{item?.label}
							</div>
						))}
					</div>,
					document.body
				)}
			</div>
		</div>
	);
}

// Export the component
export const GridContextWrapper = GridContextWrapperImpl;